// Shared quiz utilities — shuffle, key-to-index, correctness check.
//
// Extracted from mock-exam-view.tsx, quiz-view.tsx, and flashcards-view.tsx
// to eliminate 3-way duplication of shuffle() and 2-way duplication of
// keyToOptionIndex() + isCorrectAnswer().

// ── Fisher-Yates shuffle ─────────────────────────────────────────────────────

/** Fisher-Yates shuffle. Returns a new array; does not mutate the input. */
export function shuffle<T>(arr: readonly T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = out[i] as T;
    out[i] = out[j] as T;
    out[j] = tmp;
  }
  return out;
}

// ── Keyboard → option index ──────────────────────────────────────────────────

/**
 * Maps a keyboard key to a 0-based option index.
 *
 *   A / 1 → 0
 *   B / 2 → 1
 *   C / 3 → 2
 *   D / 4 → 3
 *
 * Returns null for any other key.
 */
export function keyToOptionIndex(key: string): number | null {
  const lower = key.toLowerCase();
  if (lower === 'a' || lower === '1') return 0;
  if (lower === 'b' || lower === '2') return 1;
  if (lower === 'c' || lower === '3') return 2;
  if (lower === 'd' || lower === '4') return 3;
  return null;
}

// ── Correctness ──────────────────────────────────────────────────────────────

/** Unified item shape for correctness checks (MCQ and True/False). */
export interface CheckableItem {
  kind: 'mcq' | 'tf';
  data: {
    /** MCQ: index of correct option (0-3).  TF: true=correct answer is True. */
    correct: boolean | number;
  };
}

/**
 * Returns whether `pickedIndex` is the correct answer for `item`.
 *
 * - MCQ: pickedIndex must equal `data.correct` (the correct option index).
 * - TF:  pickedIndex 0 = "True", 1 = "False"; compared against `data.correct`.
 */
/**
 * Core correctness check: given `kind`, the correct value, and the user's
 * picked index, returns whether the answer is correct.
 *
 * - MCQ: `correctValue` is the 0-based index of the correct option.
 * - TF:  `correctValue` is boolean; pickedIndex 0 = "True", 1 = "False".
 */
export function isCorrectIndex(
  kind: 'mcq' | 'tf',
  correctValue: number | boolean,
  pickedIndex: number,
): boolean {
  if (kind === 'tf') {
    return pickedIndex === 0 ? correctValue === true : correctValue === false;
  }
  return pickedIndex === (correctValue as number);
}

/** Convenience wrapper that accepts a {@link CheckableItem}. */
export function isCorrectAnswer(item: CheckableItem, pickedIndex: number): boolean {
  return isCorrectIndex(item.kind, item.data.correct, pickedIndex);
}
